import { useCallback } from 'react';
import XlinkAPI from '~/app/api/xlinkAPI';
import ErrorHelpers from '~/app/errorHelpers.js';
import { statusOK } from '~/app/webHelpers.js';
import { openWallet } from '~/app/Pages/Payments/helpers/paymentHelpers.js';

/**
 * useWallet
 *
 * Owns the four wallet-related methods that were previously class methods on
 * FormViewer:
 *
 *   SendRemotePayment(showSnackBar) — triggers remote payment via calcAPI
 *   gotoWallet()                   — fetches wallet tokens and opens the wallet
 *   getReturnGUID()                — fetches return GUID (strips hyphens)
 *   getWalletToken(isRemoteInvoice) — fetches wallet token/secToken pair
 *
 * Returns:
 *   { SendRemotePayment, gotoWallet, getReturnGUID, getWalletToken }
 */
const useWallet = ({
  calcAPIRef,
  payloadRef, // kept-current ref to this.payload (season, isDrilldown source)
  // Props read at call-time
  returnIDRef, // kept-current ref to props.returnID
  officeProfileRef, // kept-current ref to props.officeProfile
  ddhRef, // kept-current ref to props.ddh
  // Hook results (kept-current via ref pattern)
  snackbarsRef, // kept-current ref to props.snackbars
  showErrorDialogRef, // kept-current ref to props.showErrorDialog
}) => {
  /**
   * Sends a remote payment via calcAPI. Optionally shows a snackbar prompt.
   *
   * @param {boolean} [shouldShowSnackBar=true]
   */
  const SendRemotePayment = useCallback(
    (shouldShowSnackBar = true) => {
      calcAPIRef.current?.SendRemotePayment();
      if (shouldShowSnackBar) {
        snackbarsRef.current?.showPromptSnackbar('Remote Invoice has been sent');
      }
    },
    [calcAPIRef, snackbarsRef],
  );

  /**
   * Fetches wallet tokens and opens the wallet iframe/window. Shows an error
   * dialog if the wallet cannot be reached or tokens are missing.
   */
  const gotoWallet = useCallback(async () => {
    const isDrilldown = ddhRef.current.length > 1;
    const officeID = officeProfileRef.current.office_id;
    let walletData = null;

    try {
      const res = await XlinkAPI.getWallet(officeID, returnIDRef.current, false, false);
      if (res?.data) {
        walletData = res.data;
      }
    } catch (err) {
      if (err?.response?.status === 401) {
        showErrorDialogRef.current(
          'Insufficient wallet funds. Please contact a user with wallet access to add funds to your wallet.',
        );
      } else {
        showErrorDialogRef.current(
          'Unable to connect to your wallet at this time. Please contact technical support if the problem persists.',
        );
      }
      return;
    }

    let walletToken = '';
    let walletSecToken = '';
    const walletType = 'office';

    const wallets = walletData?.wallets;
    wallets?.forEach(wallet => {
      if (wallet?.walletType === walletType) {
        walletToken = wallet.token;
        walletSecToken = wallet.secToken;
      }
    });

    if (walletToken && walletSecToken) {
      openWallet(walletToken, walletSecToken, payloadRef.current?.season, isDrilldown);
    } else {
      showErrorDialogRef.current(
        'Unable to connect to your wallet at this time. Please contact technical support if the problem persists.',
      );
    }
  }, [ddhRef, officeProfileRef, payloadRef, returnIDRef, showErrorDialogRef]);

  /**
   * Fetches the return GUID from the API, stripping hyphens before returning.
   *
   * @returns {Promise<string|undefined>}
   */
  const getReturnGUID = useCallback(async () => {
    try {
      const res = await XlinkAPI.getReturnGUID(returnIDRef.current);
      if (statusOK(res)) {
        const returnGUID = res.data;
        return returnGUID.replaceAll('-', '');
      }
    } catch (error) {
      ErrorHelpers.handleError('Error fetching invoice data: ', error);
    }
  }, [returnIDRef]);

  /**
   * Fetches the wallet token and secToken pair for use in payment flows.
   *
   * @param {boolean} [isRemoteInvoice=false]
   * @returns {Promise<{ walletToken: string, secToken: string }|undefined>}
   */
  const getWalletToken = useCallback(
    async (isRemoteInvoice = false) => {
      const officeID = officeProfileRef.current.office_id;
      try {
        const response = await XlinkAPI.getWallet(
          officeID,
          returnIDRef.current,
          isRemoteInvoice,
          true,
        );
        if (response) {
          let token = '';
          let secToken = '';
          let walletType = '';

          if (response.data?.walletAccountOverride) {
            walletType = 'account';
          } else {
            walletType = 'office';
          }

          const wallets = response.data?.wallets;
          wallets?.forEach(wallet => {
            if (wallet?.walletType === walletType) {
              token = wallet.token;
              secToken = wallet.secToken;
            }
          });
          return {
            walletToken: token,
            secToken,
          };
        }
      } catch (error) {
        ErrorHelpers.handleError('Unable to Fetch Wallet Information properly: ', error);
      }
    },
    [officeProfileRef, returnIDRef],
  );

  return {
    SendRemotePayment,
    gotoWallet,
    getReturnGUID,
    getWalletToken,
  };
};

export default useWallet;
